import { css } from "vite-css-in-js"
import { computed, type ButtonHTMLAttributes } from "vue"
import { defineComponent, inferProps, propsWithDefaults, renderSlot, type ReactiveComponent } from "vue-utils"

export type BootstrapButtonColorType =
	| "primary"
	| "secondary"
	| "success"
	| "danger"
	| "warning"
	| "info"
	| "light"
	| "dark"
	| "decider"

export type BootstrapButtonType = "button" | "submit"

export interface Props {
	color: BootstrapButtonColorType
	type?: BootstrapButtonType
	plain?: boolean
	isSubmitting?: boolean
	small?: boolean
}

const btnStyles = css`
	display: inline-flex;
	justify-content: center;
	align-items: center;
	gap: 0.5rem;
	font-size: 14pt;
	border-radius: 0px;

	& > .spinner-border {
		width: 1.125em;
		height: 1.125em;
	}
`

const BootstrapButton: ReactiveComponent<Props, ButtonHTMLAttributes> = (initialProps, { slots, attrs }) => {
	const props = propsWithDefaults(initialProps, {
		type: "button",
		isSubmitting: false,
		plain: false,
	})

	const classNames = computed(() => {
		const names: string[] = ["btn", `btn-${props.color}`]
		if (props.plain) {
			names.push("btn-plain")
		}
		if (props.small) {
			names.push("btn-sm")
		}
		names.push(btnStyles)
		return names
	})

	return () => (
		<button class={classNames.value} type={props.type} {...attrs} disabled={attrs.disabled || props.isSubmitting}>
			{props.isSubmitting ? <span aria-hidden="true" class="align-middle spinner-border" /> : null}
			{renderSlot(slots)}
		</button>
	)
}

export default defineComponent(BootstrapButton, inferProps<Props>())
